import { useEffect, useRef, useState } from "react";

// Web Speech API wrappers — Arabic/English/French supported natively in Chrome, Edge, Safari.
// Returns { supported, listening, transcript, start, stop } for STT.
export function useSpeechRecognition({ lang = "ar", onFinal }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [supported, setSupported] = useState(false);
  const recogRef = useRef(null);

  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) return;
    setSupported(true);
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.lang = lang === "ar" ? "ar-SA" : lang === "fr" ? "fr-FR" : "en-US";
    r.onresult = (ev) => {
      let text = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        text += ev.results[i][0].transcript;
      }
      setTranscript(text);
      const last = ev.results[ev.results.length - 1];
      if (last?.isFinal && onFinal) onFinal(text);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recogRef.current = r;
    return () => { try { r.abort(); } catch (err) { console.warn("SR abort failed", err); } };
  }, [lang, onFinal]);

  const start = () => {
    if (!recogRef.current) return;
    setTranscript("");
    try {
      recogRef.current.start();
      setListening(true);
    } catch (err) { console.warn("SR start failed", err); }
  };
  const stop = () => { try { recogRef.current?.stop(); } catch (err) { console.warn("SR stop failed", err); } };

  return { supported, listening, transcript, start, stop };
}

// Speak text using browser SpeechSynthesis. Returns { supported, speaking, speak, stop }.
export function useSpeechSynthesis({ lang = "ar" }) {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      setSupported(true);
    }
  }, []);

  const speak = (text) => {
    if (!supported || !text) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang === "ar" ? "ar-SA" : lang === "fr" ? "fr-FR" : "en-US";
      u.rate = 0.98;
      u.pitch = 1.0;
      // try to pick a matching voice
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find((v) => v.lang?.startsWith(u.lang.split("-")[0]));
      if (match) u.voice = match;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    } catch (err) { console.warn("TTS speak failed", err); }
  };
  const stop = () => {
    try { window.speechSynthesis.cancel(); setSpeaking(false); } catch (err) { console.warn("TTS stop failed", err); }
  };

  return { supported, speaking, speak, stop };
}
